import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
    Table,
    Typography,
    Modal,
    Button,
    Input,
    DatePicker,
    Select,
    Row,
    Col,
    Space,
} from "antd";
import ViewOneEmployeeRecord from "../hr/ViewOneEmployeeRecord";
import { CheckOutlined, CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const PAGE_SIZE = 100;

export default function TeamCompoff() {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const { authData, backUrl } = useContext(AuthContext);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showSingle, setShowSingle] = useState(false);
    const [showRejectReason, setShowRejectReason] = useState({ status: false, data: null });
    const [showAcceptReason, setShowAcceptReason] = useState({ status: false, data: null });
    const [rejectionData, setRejectionData] = useState("");
    const [approvalData, setApprovalData] = useState("");

    // Loading states
    const [tableLoading, setTableLoading] = useState(false);
    const [unapprovedLoading, setUnapprovedLoading] = useState(false);
    const [approveLoading, setApproveLoading] = useState(false);
    const [rejectLoading, setRejectLoading] = useState(false);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [unapprovedCurrentPage, setUnapprovedCurrentPage] = useState(1);
    const [unapprovedTotalCount, setUnapprovedTotalCount] = useState(0);

    // Bulk selection
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [showBulkReject, setShowBulkReject] = useState(false);
    const [showBulkApprove, setShowBulkApprove] = useState(false);
    const [bulkRejectionData, setBulkRejectionData] = useState("");
    const [bulkApprovalData, setBulkApprovalData] = useState("");

    // Unapproved compoffs state
    const [unapprovedCompoffs, setUnapprovedCompoffs] = useState([]);
    const [isUnapproved, setIsUnapproved] = useState(false);

    // Check if user is HOD
    const [isHod, setIsHod] = useState(false);

    useEffect(() => {
        if (authData?.employee?.is_hod === true) {
            setIsHod(true);
        }
    }, [authData]);

    // Filters
    const [empCodeFilter, setEmpCodeFilter] = useState("");
    const [empNameFilter, setEmpNameFilter] = useState("");
    const [dateRangeFilter, setDateRangeFilter] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");

    const [Organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [organisationFilter, setOrganisationFilter] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [designationFilter, setDesignationFilter] = useState("");

    const fetchOrganisation = async () => {
        const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setOrganisations(res.data.results || []);
    };

    const fetchDepartments = async (orgId) => {
        const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setDepartments(res.data.results || []);
    };

    const fetchDesignations = async (deptId) => {
        const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        });
        setDesignations(res.data.results || []);
    };

    useEffect(() => {
        fetchOrganisation();
    }, []);

    // Fetch regular compoff data with server-side pagination
    const fetchData = async (page = 1) => {
        setTableLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/hr/compoffs/?is_employee_view=true&page=${page}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setData(res.data.results || []);
            setTotalCount(res.data.count || 0);
            setCurrentPage(page);
        } catch (error) {
            console.error("Error fetching compoff data:", error);
            toast.error("Failed to fetch compoff data");
        } finally {
            setTableLoading(false);
        }
    };

    // Fetch unapproved compoffs with server-side pagination
    // const fetchUnapproved = async (page = 1) => {
    //     setUnapprovedLoading(true);
    //     try {
    //         const res = await axios.get(
    //             `${backUrl}/api/hr/unapproved-compoffs/?is_employee_view=true&page=${page}`,
    //             { headers: { Authorization: `Bearer ${authData?.access}` } }
    //         );
    //         setUnapprovedCompoffs(res.data.results || []);
    //         setUnapprovedTotalCount(res.data.count || 0);
    //         setUnapprovedCurrentPage(page);
    //     } catch (error) {
    //         console.error("Error fetching unapproved compoffs:", error);
    //     } finally {
    //         setUnapprovedLoading(false);
    //     }
    // };

    useEffect(() => {
        fetchData(1);
        // fetchUnapproved(1);
    }, []);

    // Apply client-side filters on current page data
    const applyFilters = () => {
        let temp = isUnapproved ? [...unapprovedCompoffs] : [...data];

        if (empCodeFilter) {
            temp = temp.filter(item =>
                item.employee?.user?.username?.toLowerCase().includes(empCodeFilter.toLowerCase())
            );
        }

        if (empNameFilter) {
            temp = temp.filter(item =>
                item.employee?.user?.first_name?.toLowerCase().includes(empNameFilter.toLowerCase())
            );
        }

        if (organisationFilter) {
            temp = temp.filter(item => item.employee?.organisation_name === organisationFilter);
        }

        if (departmentFilter) {
            temp = temp.filter(item => item.employee?.department_name === departmentFilter);
        }

        if (designationFilter) {
            temp = temp.filter(item => item.employee?.designation_name === designationFilter);
        }

        if (dateRangeFilter.length === 2) {
            const [start, end] = dateRangeFilter;
            temp = temp.filter(item => {
                const workDate = dayjs(item.work_date);
                return workDate.isSameOrAfter(start, "day") && workDate.isSameOrBefore(end, "day");
            });
        }

        if (statusFilter) {
            temp = temp.filter(item => item.status === statusFilter);
        }

        setFilteredData(temp);
    };

    useEffect(() => {
        applyFilters();
    }, [
        empCodeFilter,
        empNameFilter,
        dateRangeFilter,
        organisationFilter,
        departmentFilter,
        designationFilter,
        statusFilter,
        data,
        unapprovedCompoffs,
        isUnapproved,
    ]);

    const handlePageChange = (page) => {
        if (isUnapproved) {
            // fetchUnapproved(page);
        } else {
            fetchData(page);
        }
        setSelectedRowKeys([]);
    };

    // Handle Approve
    const handleApprove = async (id) => {
        setApproveLoading(true);
        const approveApi = `${backUrl}/api/hr/compoffs/approve-work/?is_employee_view=true`;
        const unApprovedApi = `${backUrl}/api/hr/unapproved-compoffs/${id}/admin-update-status/`;

        try {
            let res;
            if (isUnapproved) {
                res = await axios.post(unApprovedApi, { status: "Approved", remarks: approvalData }, {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                });
            } else {
                res = await axios.post(approveApi, { compoff_ids: [id], rejection_reason: approvalData }, {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                });
            }

            toast.success(res?.data?.message || res?.data?.detail || "Compoff Approved Successfully");
            setApprovalData("");
            setShowAcceptReason({ status: false, data: null });
            await fetchData(currentPage);
            // await fetchUnapproved(unapprovedCurrentPage);
        } catch (error) {
            toast.error(error?.response?.data?.error || error?.response?.data?.detail || "Approval failed");
        } finally {
            setApproveLoading(false);
        }
    };

    // Handle Reject
    const handleReject = async (id) => {
        setRejectLoading(true);
        const rejectApi = `${backUrl}/api/hr/compoffs/reject-work/?is_employee_view=true`;
        const unApprovedApi = `${backUrl}/api/hr/unapproved-compoffs/${id}/admin-update-status/`;

        try {
            let res;
            if (isUnapproved) {
                res = await axios.post(unApprovedApi, { status: "Rejected", remarks: rejectionData }, {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                });
            } else {
                res = await axios.post(rejectApi, { compoff_ids: [id], rejection_reason: rejectionData }, {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                });
            }

            toast.success(res.data?.message || "Compoff Rejected Successfully");
            setRejectionData("");
            setShowRejectReason({ status: false, data: null });
            await fetchData(currentPage);
            // await fetchUnapproved(unapprovedCurrentPage);
        } catch (error) {
            const errData = error?.response?.data;
            let errorMsg = "Rejection failed";
            if (typeof errData === "string") errorMsg = errData;
            else if (errData?.error) errorMsg = errData.error;
            else if (errData?.detail) errorMsg = errData.detail;
            else if (typeof errData === "object") errorMsg = Object.values(errData).flat().join(", ");
            toast.error(errorMsg);
        } finally {
            setRejectLoading(false);
        }
    };

    // Bulk Approve
    const handleBulkApprove = async () => {
        if (selectedRowKeys.length === 0) { toast.warning("Please select at least one record"); return; }
        setApproveLoading(true);
        try {
            const res = await axios.post(
                `${backUrl}/api/hr/compoffs/approve-work/?is_employee_view=true`,
                { compoff_ids: selectedRowKeys, rejection_reason: bulkApprovalData },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success(res?.data?.message || res?.data?.detail || "Compoffs approved successfully");
            setBulkApprovalData("");
            setShowBulkApprove(false);
            setSelectedRowKeys([]);
            await fetchData(currentPage);
            // await fetchUnapproved(unapprovedCurrentPage);
        } catch (error) {
            toast.error(error?.response?.data?.error || error?.response?.data?.detail || "Bulk approval failed");
        } finally {
            setApproveLoading(false);
        }
    };

    // Bulk Reject
    const handleBulkReject = async () => {
        if (selectedRowKeys.length === 0) { toast.warning("Please select at least one record"); return; }
        if (!bulkRejectionData.trim()) { toast.warning("Please provide a reason for rejection"); return; }
        setRejectLoading(true);
        try {
            const res = await axios.post(
                `${backUrl}/api/hr/compoffs/reject-work/?is_employee_view=true`,
                { compoff_ids: selectedRowKeys, rejection_reason: bulkRejectionData },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            toast.success(res?.data?.message || res?.data?.detail || "Compoffs rejected successfully");
            setBulkRejectionData("");
            setShowBulkReject(false);
            setSelectedRowKeys([]);
            await fetchData(currentPage);
            // await fetchUnapproved(unapprovedCurrentPage);
        } catch (error) {
            toast.error(error?.response?.data?.error || error?.response?.data?.detail || "Bulk rejection failed");
        } finally {
            setRejectLoading(false);
        }
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
        getCheckboxProps: (record) => ({ name: record.id }),
    };

    const isLoading = isUnapproved ? unapprovedLoading : tableLoading;
    const activePage = isUnapproved ? unapprovedCurrentPage : currentPage;
    const activeTotal = isUnapproved ? unapprovedTotalCount : totalCount;

    const columns = [
        {
            title: "Employee Code",
            dataIndex: ["employee", "emp_code"],
            key: "employee_code",
            render: (text, record) => (
                <a onClick={() => { setSelectedEmployee(record); setShowSingle(true); }}>{text}</a>
            ),
            fixed: "left",
            width: 130,
        },
        { title: "Employee Name", dataIndex: ["employee", "full_name"] },

        { title: "Work Date", dataIndex: "work_date", key: "work_date" },
        { title: "Expiry Date", dataIndex: "expiry_date", key: "expiry_date" },
        {
            title: "Reason",
            key: "reason",
            render: (_, record) => (
                <>
                    <div style={{ whiteSpace: "normal", wordWrap: "break-word", maxWidth: "400px" }}>
                        {record.reason}
                    </div>
                    <br /><br />
                    {record.cancellation_reason && (
                        <div style={{ marginTop: "4px", fontWeight: 600, color: "red", backgroundColor: "#ffe6e6", padding: "3px 6px", borderRadius: "6px", display: "inline-block" }}>
                            Cancellation Reason: {record.cancellation_reason}
                        </div>
                    )}
                </>
            ),
        },
        {
            title: "Status",
            key: "status",
            render: (_, record) => {
                const status = record.status;
                return (
                    <span style={{
                        fontWeight: 500,
                        color: status === "Available" ? "green" : status === "Rejected" ? "red" : "#faad14",
                    }}>
                        {status}
                        {record?.availed_leaves.length >= 1 && (
                            <div style={{ marginTop: "4px", fontSize: "12px", color: "#1890ff" }}>
                                <strong>Availed Date:</strong> {record.availed_leaves[0].start_date}
                            </div>
                        )}
                    </span>
                );
            },
        },
        { title: "Organisation", dataIndex: ["employee", "organisation_name"], key: "organisation_name" },
        { title: "Department", dataIndex: ["employee", "department_name"], key: "department_name" },
        { title: "Designation", dataIndex: ["employee", "designation_name"], key: "designation_name" },
        // {
        //     title: "Approve",
        //     key: "approve",
        //     render: (_, record) => (
        //         <Button
        //             type="primary"
        //             shape="circle"
        //             icon={<CheckOutlined />}
        //             style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
        //             onClick={() => setShowAcceptReason({ status: true, data: record.id })}
        //         />
        //     ),
        // },
        // {
        //     title: "Reject",
        //     key: "reject",
        //     render: (_, record) => (
        //         <Button
        //             danger
        //             type="primary"
        //             shape="circle"
        //             icon={<CloseOutlined />}
        //             onClick={() => setShowRejectReason({ status: true, data: record.id })}
        //         />
        //     ),
        // },
    ];

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />
            <center><Title level={5}>Team Off Day Working</Title></center>

            {/* Tab Buttons */}
            {/* {authData?.employee?.is_hod && (
                <div style={{ textAlign: "center", marginBottom: "20px" }}>
                    <Button
                        type={!isUnapproved ? "primary" : "default"}
                        onClick={() => { setIsUnapproved(false); setSelectedRowKeys([]); }}
                        style={{ marginRight: "10px" }}
                    >
                        {!isUnapproved && tableLoading
                            ? <><LoadingOutlined style={{ marginRight: 6 }} />All Compoffs</>
                            : "All Compoffs"}
                    </Button>
                    <Button
                        type={isUnapproved ? "primary" : "default"}
                        onClick={() => { setIsUnapproved(true); setSelectedRowKeys([]); }}
                        style={{
                            backgroundColor: isUnapproved ? "blue" : undefined,
                            borderColor: isUnapproved ? "blue" : undefined,
                        }}
                    >
                        {isUnapproved && unapprovedLoading
                            ? <><LoadingOutlined style={{ marginRight: 6 }} />Unapproved</>
                            : "Unapproved"}
                    </Button>
                </div>
            )} */}

            {/* Filters */}
            <div className="filters-container">
                <Row gutter={[12, 12]} justify="center">
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input placeholder="Employee Code" value={empCodeFilter} onChange={e => setEmpCodeFilter(e.target.value)} />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input placeholder="Employee Name" value={empNameFilter} onChange={e => setEmpNameFilter(e.target.value)} />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Select Organisation"
                            style={{ width: "100%" }}
                            value={organisationFilter || undefined}
                            allowClear
                            onChange={(value) => {
                                setOrganisationFilter(value);
                                setDepartmentFilter("");
                                setDesignationFilter("");
                                setDepartments([]);
                                setDesignations([]);
                                if (value) {
                                    const orgObj = Organisations.find(o => o.name === value);
                                    if (orgObj) fetchDepartments(orgObj.id);
                                }
                            }}
                        >
                            {Organisations.map(org => <Option key={org.id} value={org.name}>{org.name}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            showSearch
                            placeholder="Select Department"
                            style={{ width: "100%" }}
                            value={departmentFilter || undefined}
                            allowClear
                            disabled={!organisationFilter}
                            onChange={(value) => {
                                setDepartmentFilter(value);
                                setDesignationFilter("");
                                setDesignations([]);
                                if (value) {
                                    const deptObj = departments.find(d => d.name === value);
                                    if (deptObj) fetchDesignations(deptObj.id);
                                }
                            }}
                            filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}
                        >
                            {departments.map(dept => <Option key={dept.id} value={dept.name}>{dept.name}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Select Designation"
                            style={{ width: "100%" }}
                            value={designationFilter || undefined}
                            allowClear
                            disabled={!departmentFilter}
                            onChange={(value) => setDesignationFilter(value)}
                            showSearch
                            filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}
                        >
                            {designations.map(des => <Option key={des.id} value={des.name}>{des.name}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Select Status"
                            style={{ width: "100%" }}
                            value={statusFilter || undefined}
                            allowClear
                            onChange={(value) => setStatusFilter(value)}
                        >
                            <Option value="Available">Available</Option>
                            <Option value="Availed">Availed</Option>
                            <Option value="Expired">Expired</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <RangePicker value={dateRangeFilter} onChange={dates => setDateRangeFilter(dates || [])} style={{ width: "100%" }} />
                    </Col>
                </Row>
            </div>

            {/* Bulk Action Buttons */}
            {selectedRowKeys.length > 0 && (
                <div style={{ margin: "20px 0", textAlign: "center" }}>
                    <Space>
                        <span style={{ fontWeight: "500" }}>
                            {selectedRowKeys.length} item{selectedRowKeys.length > 1 ? "s" : ""} selected
                        </span>
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                            onClick={() => setShowBulkApprove(true)}
                        >
                            Bulk Approve
                        </Button>
                        <Button danger type="primary" icon={<CloseOutlined />} onClick={() => setShowBulkReject(true)}>
                            Bulk Reject
                        </Button>
                        <Button onClick={() => setSelectedRowKeys([])}>Clear Selection</Button>
                    </Space>
                </div>
            )}

            {/* Table */}
            <div className="table-responsive">
                <Table
                    dataSource={filteredData}
                    columns={columns}
                    rowKey="id"
                    // rowSelection={rowSelection}
                    loading={{
                        spinning: isLoading,
                        indicator: <LoadingOutlined style={{ fontSize: 36, color: "#0e497b" }} spin />,
                        tip: "Loading...",
                    }}
                    pagination={{
                        current: activePage,
                        pageSize: PAGE_SIZE,
                        total: activeTotal,
                        showSizeChanger: false,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                        onChange: handlePageChange,
                    }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                    bordered
                    scroll={{ x: "max-content" }}
                />
            </div>

            {/* Individual Reject Modal */}
            <Modal title="Reason for Rejection" open={showRejectReason.status} footer={null} onCancel={() => setShowRejectReason({ status: false, data: null })} width={600} centered>
                <Input.TextArea rows={4} placeholder="Enter reason here..." value={rejectionData} onChange={(e) => setRejectionData(e.target.value)} style={{ borderRadius: "8px", marginBottom: "16px" }} />
                <div style={{ textAlign: "right" }}>
                    <Button style={{ marginRight: "8px" }} onClick={() => setShowRejectReason({ status: false, data: null })} disabled={rejectLoading}>Cancel</Button>
                    <Button type="primary" danger loading={rejectLoading} disabled={rejectLoading} onClick={() => handleReject(showRejectReason.data)}>
                        {rejectLoading ? "Rejecting..." : "Reject"}
                    </Button>
                </div>
            </Modal>

            {/* Individual Approve Modal */}
            <Modal title="Approval Remarks" open={showAcceptReason.status} footer={null} onCancel={() => setShowAcceptReason({ status: false, data: null })} width={600} centered>
                <Input.TextArea rows={4} placeholder="Enter remarks here (optional)..." value={approvalData} onChange={(e) => setApprovalData(e.target.value)} style={{ borderRadius: "8px", marginBottom: "16px" }} />
                <div style={{ textAlign: "right" }}>
                    <Button style={{ marginRight: "8px" }} onClick={() => setShowAcceptReason({ status: false, data: null })} disabled={approveLoading}>Cancel</Button>
                    <Button type="primary" loading={approveLoading} disabled={approveLoading} style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }} onClick={() => handleApprove(showAcceptReason.data)}>
                        {approveLoading ? "Approving..." : "Approve"}
                    </Button>
                </div>
            </Modal>

            {/* Bulk Approve Modal */}
            <Modal title={`Bulk Approve (${selectedRowKeys.length} compoffs)`} open={showBulkApprove} footer={null} onCancel={() => { setShowBulkApprove(false); setBulkApprovalData(""); }} width={600} centered>
                <Input.TextArea rows={4} placeholder="Enter common remarks for all selected compoffs (optional)..." value={bulkApprovalData} onChange={(e) => setBulkApprovalData(e.target.value)} style={{ borderRadius: "8px", marginBottom: "16px" }} />
                <div style={{ textAlign: "right" }}>
                    <Button style={{ marginRight: "8px" }} onClick={() => { setShowBulkApprove(false); setBulkApprovalData(""); }} disabled={approveLoading}>Cancel</Button>
                    <Button type="primary" loading={approveLoading} disabled={approveLoading} style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }} onClick={handleBulkApprove}>
                        {approveLoading ? "Approving..." : "Approve All"}
                    </Button>
                </div>
            </Modal>

            {/* Bulk Reject Modal */}
            <Modal title={`Bulk Reject (${selectedRowKeys.length} compoffs)`} open={showBulkReject} footer={null} onCancel={() => { setShowBulkReject(false); setBulkRejectionData(""); }} width={600} centered>
                <Input.TextArea rows={4} placeholder="Enter common rejection reason for all selected compoffs..." value={bulkRejectionData} onChange={(e) => setBulkRejectionData(e.target.value)} style={{ borderRadius: "8px", marginBottom: "16px" }} />
                <div style={{ textAlign: "right" }}>
                    <Button style={{ marginRight: "8px" }} onClick={() => { setShowBulkReject(false); setBulkRejectionData(""); }} disabled={rejectLoading}>Cancel</Button>
                    <Button type="primary" danger loading={rejectLoading} disabled={rejectLoading || !bulkRejectionData.trim()} onClick={handleBulkReject}>
                        {rejectLoading ? "Rejecting..." : "Reject All"}
                    </Button>
                </div>
            </Modal>

            {/* Employee Record Modal */}
            <Modal open={showSingle} footer={null} onCancel={() => setShowSingle(false)} width={1300}>
                <ViewOneEmployeeRecord employee={selectedEmployee} />
            </Modal>
        </>
    );
}